import {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { post, get } from "lib/api";
import { isPortalRole } from "lib/portalRoles";
import { cacheCompanyProfiles } from "lib/companyProfile";
import { useStorefrontTenant } from "context/StorefrontTenantContext";

const AuthCtx = createContext({
  user: null,
  profile: null,
  pluginRole: null, // users-permissions role (e.g. master/admin/authenticated)
  portalMembership: null,
  portalCapabilities: null,
  activeCompany: null, // company key string
  storefrontTenant: null,
  allowedCompanies: [],
  rolePermissions: {}, // { [roleKey]: [routeKey, ...] }
  rolePermissionsAvailable: false, // true when role-permission single type exists and was fetched
  authLoading: false,
  permissionsLoading: false,
  token: null,
  hasPermission: () => true,
  setActiveCompany: () => {},
  login: async () => {},
  loginWithSocial: async () => {},
  logout: () => {},
});

function normalizeCompanyKey(value) {
  const key = String(value || "")
    .trim()
    .toLowerCase();
  return /^[a-z0-9][a-z0-9_-]{0,63}$/.test(key) ? key : null;
}

function resolveCompanyForRole({
  role,
  allowedKeys,
  defaultKey,
  storedCompany,
}) {
  const normalizedRole = String(role || "")
    .trim()
    .toLowerCase();
  if (!normalizedRole) return null;

  const options = Array.from(
    new Set(
      (Array.isArray(allowedKeys) ? allowedKeys : [])
        .map(normalizeCompanyKey)
        .filter(Boolean)
    )
  ).sort();
  const fallbackDefault = normalizeCompanyKey(defaultKey);
  const stored = normalizeCompanyKey(storedCompany);

  // Match iOS: signed-in users are scoped by staff/portal company membership,
  // not by the public storefront domain or geolocation fallback.
  if (options.length === 1) return options[0];
  if (options.length > 1) {
    if (stored && options.includes(stored)) return stored;
    if (fallbackDefault && options.includes(fallbackDefault))
      return fallbackDefault;
    return null;
  }
  return null;
}

export function AuthProvider({ children }) {
  const { tenantKey: storefrontTenantKey } = useStorefrontTenant();
  const initialToken = (() => {
    try {
      return localStorage.getItem("jwt") || null;
    } catch {
      return null;
    }
  })();

  const [token, setToken] = useState(initialToken);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [pluginRole, setPluginRole] = useState(null);
  const [portalMembership, setPortalMembership] = useState(null);
  const [portalCapabilities, setPortalCapabilities] = useState(null);
  const [allowedCompanies, setAllowedCompanies] = useState([]);
  const [activeCompany, setActiveCompanyState] = useState(() => {
    try {
      if (!initialToken) return null;
      return normalizeCompanyKey(localStorage.getItem("activeCompany"));
    } catch {
      return null;
    }
  });
  const [rolePermissions, setRolePermissions] = useState({});
  const [rolePermissionsAvailable, setRolePermissionsAvailable] =
    useState(false);
  const previousActiveCompanyRef = useRef(activeCompany || null);
  // If a JWT is already stored (page refresh), start in loading to avoid misrouting before /users/me hydrates.
  const [authLoading, setAuthLoading] = useState(!!initialToken);
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  useEffect(() => {
    const previous = previousActiveCompanyRef.current || null;
    const next = activeCompany || null;
    if (previous === next) return;
    previousActiveCompanyRef.current = next;
    window.dispatchEvent(
      new CustomEvent("active-company-changed", { detail: { previous, next } })
    );
  }, [activeCompany]);

  const setActiveCompany = useCallback((key) => {
    const normalized = key ? String(key).trim().toLowerCase() : "";
    if (normalized) {
      localStorage.setItem("activeCompany", normalized);
      setActiveCompanyState(normalized);
    } else {
      localStorage.removeItem("activeCompany");
      setActiveCompanyState(null);
    }
  }, []);

  // Listen for token clearing events (when token is cleared elsewhere, e.g., by api interceptor on 401)
  useEffect(() => {
    const handleTokenCleared = () => {
      const storedToken = localStorage.getItem("jwt");
      if (!storedToken && token) {
        setToken(null);
      }
    };
    window.addEventListener("auth-token-cleared", handleTokenCleared);
    // Also listen for storage events (cross-tab synchronization)
    window.addEventListener("storage", handleTokenCleared);
    return () => {
      window.removeEventListener("auth-token-cleared", handleTokenCleared);
      window.removeEventListener("storage", handleTokenCleared);
    };
  }, [token]);

  useEffect(() => {
    if (token) {
      localStorage.setItem("jwt", token);
      (async () => {
        setAuthLoading(true);
        const applyHydratedCompany = (key) => {
          const normalized = normalizeCompanyKey(key);
          try {
            if (normalized) {
              localStorage.setItem("activeCompany", normalized);
            } else {
              localStorage.removeItem("activeCompany");
            }
          } catch {
            // ignore storage errors
          }
          setActiveCompanyState(normalized);
        };
        try {
          let me = null;
          let normalizedRole = null;
          let usedUsersMe = false;

          // Preferred bootstrap path (staff + normal app users)
          try {
            me = await get("/api/users/me", { populate: { role: true } });
            usedUsersMe = true;
            setUser(me);
          } catch (meError) {
            // Production safety-net: some portal roles can end up blocked from /api/users/me.
            // Fall back to storefront me so portal registration/login doesn't end on a blank page.
            if (meError?.response?.status !== 403) throw meError;
            const portalMe = await get("/api/storefront/me");
            const portalRole = String(portalMe?.role || "")
              .trim()
              .toLowerCase();
            if (!isPortalRole(portalRole)) throw meError;
            normalizedRole = portalRole;
            const customer = portalMe?.data || null;
            const primaryEmail = Array.isArray(customer?.emails)
              ? String(
                  customer.emails.find((value) => String(value || "").trim()) ||
                    ""
                )
                  .trim()
                  .toLowerCase()
              : "";
            me = {
              id: null,
              email: primaryEmail || null,
              username:
                primaryEmail ||
                String(customer?.name || "portal-user").trim() ||
                "portal-user",
              role: { type: portalRole },
            };
            setUser(me);
            setPortalMembership({
              membership: portalMe?.membership || null,
              customerKind: portalMe?.customerKind || null,
            });
            setPortalCapabilities(portalMe?.capabilities || null);
          }

          // Normalize users-permissions role
          if (!normalizedRole) {
            const r = me?.role || me?.attributes?.role;
            const codeTypeOrName = r?.code || r?.type || r?.name;
            normalizedRole = String(codeTypeOrName || "").toLowerCase() || null;
          }
          setPluginRole(normalizedRole);

          let portalAccountInfo = null;
          if (isPortalRole(normalizedRole)) {
            try {
              portalAccountInfo = await get("/api/storefront/me");
              const portalCompanyKey = normalizeCompanyKey(
                portalAccountInfo?.data?.company?.key
              );
              if (portalCompanyKey) {
                applyHydratedCompany(portalCompanyKey);
              }
              setPortalMembership({
                membership: portalAccountInfo?.membership || null,
                customerKind: portalAccountInfo?.customerKind || null,
              });
              setPortalCapabilities(portalAccountInfo?.capabilities || null);
            } catch (portalAccountError) {
              console.warn(
                "Storefront account not available:",
                portalAccountError?.response?.status ||
                  portalAccountError?.message
              );
              if (
                portalAccountError?.response?.status === 403 ||
                portalAccountError?.response?.status === 404
              ) {
                throw portalAccountError;
              }
            }
          }

          let roleCompanyInfo = null;
          try {
            roleCompanyInfo = await get("/api/storefront/staff/companies");
            cacheCompanyProfiles(roleCompanyInfo?.companyBranding);
          } catch {
            roleCompanyInfo = null;
          }

          const applyRoleCompanyDefaults = () => {
            const roleAllowedKeys = Array.isArray(roleCompanyInfo?.companies)
              ? roleCompanyInfo.companies
                  .map((k) => String(k || "").toLowerCase())
                  .filter(Boolean)
              : [];
            setAllowedCompanies(roleAllowedKeys);
            const roleDefaultKey = roleCompanyInfo?.defaultCompanyKey
              ? String(roleCompanyInfo.defaultCompanyKey).toLowerCase()
              : null;
            const storedCompany = normalizeCompanyKey(
              localStorage.getItem("activeCompany")
            );
            const nextCompany = resolveCompanyForRole({
              role: normalizedRole,
              allowedKeys: roleAllowedKeys,
              defaultKey: roleDefaultKey,
              storedCompany,
            });
            if (nextCompany !== storedCompany)
              applyHydratedCompany(nextCompany);
            if (isPortalRole(normalizedRole)) {
              setPortalMembership((prev) => ({
                membership:
                  roleCompanyInfo?.membership ||
                  portalAccountInfo?.membership ||
                  prev?.membership ||
                  null,
                customerKind:
                  roleCompanyInfo?.customerKind ||
                  portalAccountInfo?.customerKind ||
                  prev?.customerKind ||
                  null,
              }));
              setPortalCapabilities(
                (prev) =>
                  roleCompanyInfo?.capabilities ||
                  portalAccountInfo?.capabilities ||
                  prev ||
                  null
              );
            } else {
              setPortalMembership(null);
            }
          };

          // fetch profile by user id (optional - may not exist)
          if (usedUsersMe && me?.id) {
            try {
              const prof = await get("/api/user-profiles", {
                filters: { user: me.id },
                populate: { companies: true, defaultCompany: true },
              });
              const item = Array.isArray(prof?.data) ? prof.data[0] : null;
              const attrs = item?.attributes || item || null;
              const normalizedProfile =
                item && attrs ? { id: item.id ?? attrs.id, ...attrs } : null;
              setProfile(normalizedProfile);

              // Resolve/validate activeCompany against staff profile or portal membership companies.
              const companiesRel = normalizedProfile?.companies;
              const companiesData = Array.isArray(companiesRel?.data)
                ? companiesRel.data
                : Array.isArray(companiesRel)
                ? companiesRel
                : [];
              const profileAllowedKeys = companiesData
                .map((c) => c?.attributes?.key ?? c?.key)
                .filter(Boolean)
                .map((k) => String(k).toLowerCase());
              const profileDefaultKey =
                normalizedProfile?.defaultCompany?.data?.attributes?.key ??
                normalizedProfile?.defaultCompany?.attributes?.key ??
                normalizedProfile?.defaultCompany?.key ??
                null;

              const roleAllowedKeys = Array.isArray(roleCompanyInfo?.companies)
                ? roleCompanyInfo.companies
                    .map((k) => String(k || "").toLowerCase())
                    .filter(Boolean)
                : [];
              const roleDefaultKey = roleCompanyInfo?.defaultCompanyKey
                ? String(roleCompanyInfo.defaultCompanyKey).toLowerCase()
                : null;

              const allowedKeys = roleAllowedKeys.length
                ? roleAllowedKeys
                : profileAllowedKeys;
              setAllowedCompanies(allowedKeys);
              const defaultKey = roleDefaultKey || profileDefaultKey || null;

              const storedCompany = normalizeCompanyKey(
                localStorage.getItem("activeCompany")
              );
              const nextCompany = resolveCompanyForRole({
                role: normalizedRole,
                allowedKeys,
                defaultKey,
                storedCompany,
              });
              if (nextCompany !== storedCompany)
                applyHydratedCompany(nextCompany);

              if (isPortalRole(normalizedRole)) {
                setPortalMembership({
                  membership:
                    roleCompanyInfo?.membership ||
                    portalAccountInfo?.membership ||
                    null,
                  customerKind:
                    roleCompanyInfo?.customerKind ||
                    portalAccountInfo?.customerKind ||
                    null,
                });
              } else {
                setPortalMembership(null);
              }
            } catch (profileError) {
              // User profile endpoint might not exist or have permissions - not critical
              console.warn(
                "User profile not available:",
                profileError?.response?.status || profileError?.message
              );
              setProfile(null);
              applyRoleCompanyDefaults();
            }
          } else {
            setProfile(null);
            applyRoleCompanyDefaults();
          }

          // Fetch role permissions (single type). If it doesn't exist yet (404), treat as unavailable.
          setPermissionsLoading(true);
          try {
            // Use backend endpoint that returns effective permissions for current user
            const res = await get("/api/role-permission/me");
            const role = String(
              res?.role || normalizedRole || ""
            ).toLowerCase();
            const allowed = Array.isArray(res?.allowed) ? res.allowed : [];
            const available = !!res?.available;
            setRolePermissions(role ? { [role]: allowed } : {});
            setRolePermissionsAvailable(available);
          } catch (permError) {
            if (permError?.response?.status === 404) {
              setRolePermissions({});
              setRolePermissionsAvailable(false);
            } else {
              console.warn(
                "Role permissions not available:",
                permError?.response?.status || permError?.message
              );
              setRolePermissions({});
              setRolePermissionsAvailable(false);
            }
          } finally {
            setPermissionsLoading(false);
          }
        } catch (e) {
          console.error(e);
          // If token bootstrap is denied, clear it to avoid post-login blank loops.
          if (
            e?.response?.status === 401 ||
            e?.status === 401 ||
            e?.response?.status === 403 ||
            e?.status === 403
          ) {
            setToken(null);
            localStorage.removeItem("jwt");
          }
          setUser(null);
          setProfile(null);
          setPluginRole(null);
          setPortalMembership(null);
          setPortalCapabilities(null);
          setAllowedCompanies([]);
          setRolePermissions({});
          setRolePermissionsAvailable(false);
        } finally {
          setAuthLoading(false);
        }
      })();
    } else {
      localStorage.removeItem("jwt");
      localStorage.removeItem("cart");
      localStorage.removeItem("activeCompany");
      setUser(null);
      setProfile(null);
      setPluginRole(null);
      setPortalMembership(null);
      setPortalCapabilities(null);
      setAllowedCompanies([]);
      setActiveCompanyState(null);
      setRolePermissions({});
      setRolePermissionsAvailable(false);
      setAuthLoading(false);
      setPermissionsLoading(false);
      window.dispatchEvent(new CustomEvent("cart-cleared"));
    }
  }, [token]);

  const login = async (identifier, password) => {
    // Avoid flashing the wrong UI between token write and /users/me hydration.
    setAuthLoading(true);
    const res = await post("/api/auth/local", { identifier, password });
    const jwt = res?.jwt || null;
    if (jwt) localStorage.setItem("jwt", jwt);
    setToken(jwt);
    return !!jwt;
  };
  const loginWithSocial = async (payload) => {
    setAuthLoading(true);
    const res = await post("/api/storefront/auth/social", payload || {});
    const jwt = res?.jwt || null;
    if (jwt) localStorage.setItem("jwt", jwt);
    setToken(jwt);
    return !!jwt;
  };
  const logout = () => {
    try {
      localStorage.removeItem("cart");
      localStorage.removeItem("activeCompany");
    } catch {
      // ignore storage errors
    }
    window.dispatchEvent(new CustomEvent("cart-cleared"));
    setActiveCompanyState(null);
    setToken(null);
  };

  const hasPermission = useMemo(() => {
    return (permissionKey) => {
      if (!permissionKey) return true;
      // Master/admin always allowed (hard override)
      const role = String(pluginRole || "").toLowerCase();
      if (role === "master" || role === "admin") return true;
      // Authenticated users should always land on the AuthDashboard
      if (
        role === "authenticated" &&
        (permissionKey === "auth-dashboard" || permissionKey === "dashboard")
      ) {
        return true;
      }
      // If role-permission single type isn't set up yet, keep legacy behavior (allow)
      if (!rolePermissionsAvailable) return true;
      const allowed = rolePermissions?.[role];
      if (!Array.isArray(allowed)) return false;
      if (allowed.includes(permissionKey)) return true;

      // Compatibility aliases:
      // V1 used "dashboard"; staff-erp uses "accounting-home" for the same home dashboard grant.
      if (permissionKey === "dashboard" && allowed.includes("accounting-home"))
        return true;
      if (permissionKey === "accounting-home" && allowed.includes("dashboard"))
        return true;
      // Historically "Inventory Orders" was gated by "inventory". Allow legacy roles to keep working.
      if (permissionKey === "inventory-orders" && allowed.includes("inventory"))
        return true;

      return false;
    };
  }, [pluginRole, rolePermissions, rolePermissionsAvailable]);

  const value = useMemo(
    () => ({
      token,
      user,
      profile,
      pluginRole,
      portalMembership,
      portalCapabilities,
      activeCompany,
      storefrontTenant: storefrontTenantKey || null,
      allowedCompanies,
      rolePermissions,
      rolePermissionsAvailable,
      authLoading,
      permissionsLoading,
      hasPermission,
      setActiveCompany,
      login,
      loginWithSocial,
      logout,
    }),
    [
      token,
      user,
      profile,
      pluginRole,
      portalMembership,
      portalCapabilities,
      activeCompany,
      storefrontTenantKey,
      allowedCompanies,
      rolePermissions,
      rolePermissionsAvailable,
      authLoading,
      permissionsLoading,
      hasPermission,
      setActiveCompany,
      loginWithSocial,
    ]
  );
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function useAuth() {
  return useContext(AuthCtx);
}

export function RequireAuth({ children, roles }) {
  const { token, profile } = useAuth();
  if (!token) return null; // App routes already redirect unauthenticated to sign-in
  if (roles && profile && !roles.includes(profile.appRole)) return null;
  return children;
}

RequireAuth.propTypes = {
  children: PropTypes.node,
  roles: PropTypes.arrayOf(PropTypes.string),
};
